import React from "react";
import { PreviewTemplate } from "../../../components/Preview/PreviewTemplate";
import { Box, Button, Stack, Typography } from "@mui/material";
import WhatsappBgImg from "../../../assets/whatsapp_bg_img.jpg";
import {
  Create as CreateIcon,
  AutoAwesome as AutoAwesomeIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import SendIcon from "@mui/icons-material/Send";
import AddIcon from "@mui/icons-material/Add";
import { ManageWhatsAppTemplateRightPanel } from "../../WhatsAppTemplate/ManageWhatsAppTemplateRightPanel";
import { useDispatchWrapper } from "../../../hooks";
import { useBillingCompanyContext } from "../../../providers";
import { RecommendedTemplatesTable } from "./RecommendedTemplatesTable";
import { showMessage } from "../../../redux/messages/messagesActions";
import LoadingDialogWithMessage from "../../../components/Dialogs/LoadingDialogWithMessage";
import { fetchWhatsAppTemplateListAsync } from "../../../redux/whatsappTemplate/whatsappTemplateAction";
import { PageLoader } from "../../../components/PageLoader/PageLoader";
import {
  IWhatsAppTemplate,
  IWhatsAppTemplateData,
} from "../../../redux/whatsappTemplate/whatsappTemplate.types";
import { INITIAL_WHATSAPP_TEMPLATE_DATA } from "../../../redux/whatsappTemplate/defaultState";
import { UpsellAnimationButton } from "../../upselling/components/UpsellAnimationButton";
import { ILead } from "../../../redux/leads/leads.types";
import { ViewWhatsappDetails } from "../../leads/components/viewWhatsappDetails";

interface IRenderRecommendedTemplatesProps {
  recommendedTemplate: IWhatsAppTemplate[] | null;
  onTemplateCreateSuccess: (templateData: IWhatsAppTemplateData) => void;
  onHandleRecommendedTemplatesClick: () => void;
  showCardsView: boolean;
  onToggleCardsView: (showCardsView: boolean) => void;
  templateLoading: boolean;
  leadData: ILead | null;
  toNumber: string | null;
}

export const RenderRecommendedTemplates = (
  props: IRenderRecommendedTemplatesProps,
) => {
  const {
    recommendedTemplate,
    onTemplateCreateSuccess,
    onHandleRecommendedTemplatesClick,
    showCardsView,
    templateLoading,
    leadData,
    toNumber,
  } = props;

  const dispatch = useDispatchWrapper();
  const company = useBillingCompanyContext();

  const [validMetaLoading, setValidMetaLoading] = React.useState(false);
  const [openWhatsapp, setOpenWhatsapp] = React.useState<ILead | null>(null);
  const [templateMessage, setTemplateMessage] = React.useState<string>("");

  const [
    openManageRecommendedWhatsAppTemplate,
    setOpenManageRecommendedWhatsAppTemplate,
  ] = React.useState<IWhatsAppTemplateData | null>(null);

  const handleTemplateSaveSuccess = (templateData: IWhatsAppTemplateData) => {
    setOpenManageRecommendedWhatsAppTemplate(null);
    setValidMetaLoading(true);
    dispatch(
      fetchWhatsAppTemplateListAsync(
        1, // pageNumber
        10, // rowsInPerPage
        10, // limit
        "", // status
        { fromDate: "", toDate: "" }, // date
        [], // searchValue
        company.comapny.company_outlet_uuid, // company_outlet_uuid
        templateData?.name, // searchByName
        undefined,
        undefined,
        (data) => {
          if (data.length > 0) {
            const convertedTemplate = convertToWhatsAppTemplateData(data[0]);
            onTemplateCreateSuccess?.(convertedTemplate);
          } else {
            setValidMetaLoading(false);
            dispatch(
              showMessage({
                type: "error",
                message:
                  "Template is not created. Please try again with a different template name",
                displayAs: "snackbar",
              }),
            );
          }
        }, // searchByTemplateId
      ),
    );

    setValidMetaLoading(false);
  };

  const handleCreateTemplate = (templateData: IWhatsAppTemplateData) => {
    setOpenManageRecommendedWhatsAppTemplate(templateData);
  };

  const handleOpenWhatsapp = (templateData: IWhatsAppTemplateData) => {
    setOpenWhatsapp(leadData);
    setTemplateMessage(templateData?.body?.text);
  };
  const getBodyVariableValues = (
    template: IWhatsAppTemplate,
  ): { [key: string]: string } => {
    const components = template.components || [];
    const firstComponentArray = Array.isArray(components[0])
      ? components[0]
      : components;
    const bodyComponent = firstComponentArray.find(
      (comp: any) => comp.type === "BODY",
    );

    if (bodyComponent?.example?.body_text) {
      const bodyText = bodyComponent.example.body_text;
      const variableValues: { [key: string]: string } = {};

      bodyText.forEach((value: string, index: number) => {
        variableValues[(index + 1).toString()] = value;
      });

      return variableValues;
    }

    return {};
  };

  const convertToWhatsAppTemplateData = (
    template: IWhatsAppTemplate,
  ): IWhatsAppTemplateData => {
    const components = template.components || [];

    const firstComponentArray = Array.isArray(components[0])
      ? components[0]
      : components;

    // Extract different component types
    const bodyComponent = firstComponentArray.find(
      (comp: any) => comp.type === "BODY",
    );
    const headerComponent = firstComponentArray.find(
      (comp: any) => comp.type === "HEADER",
    );
    const footerComponent = firstComponentArray.find(
      (comp: any) => comp.type === "FOOTER",
    );
    const buttonsComponent = firstComponentArray.find(
      (comp: any) => comp.type === "BUTTONS",
    );

    return {
      name: template.name || "",
      category: template.category || "MARKETING",
      company_outlet_uuid: template.company_outlet_uuid || "",
      components: firstComponentArray,
      example: {
        header_text: bodyComponent?.example?.body_text || [],
        body_text: bodyComponent?.example?.body_text || [],
      },
      header: headerComponent || {
        type: "HEADER",
        format: null,
        text: null,
        example: {
          header_text: [],
          header_handle: null,
        },
      },
      body: bodyComponent || {
        type: "BODY",
        text: "",
        example: {
          body_text: [],
        },
      },
      footer: footerComponent || {
        type: "FOOTER",
        text: "",
      },
      buttons: buttonsComponent || {
        type: "BUTTONS",
        buttons: [],
      },
      status: template.status,
      meta_id: template.meta_id,
    };
  };

  // Convert recommended templates to WhatsApp template data format for table view
  const convertedTemplates = recommendedTemplate
    ? recommendedTemplate.map(convertToWhatsAppTemplateData)
    : [];

  // Handle template add action (same as card view add button)
  const handleTemplateAdd = (template: IWhatsAppTemplateData) => {
    handleCreateTemplate(template);
  };

  return (
    <PageLoader loading={templateLoading}>
      <Box mb={4} textAlign={"end"}>
        <UpsellAnimationButton
          onClick={onHandleRecommendedTemplatesClick}
          label="Refresh"
          startIcon={<RefreshIcon />}
        />
      </Box>

      {showCardsView ? (
        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          {/* Create New Template Card */}
          {recommendedTemplate && recommendedTemplate.length > 0 ? (
            <Box
              sx={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {/* Create New Template Card */}
              <Box
                sx={{
                  backgroundColor: "#efeae2",
                  backgroundImage: `url(${WhatsappBgImg})`,
                  backgroundRepeat: "repeat",
                  backgroundSize: "auto",
                  // backgroundPosition: "center",
                  backgroundBlendMode: "multiply",
                  borderRadius: 2,
                  position: "relative",
                  width: "380px",
                  height: "450px",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  transition: "all 0.3s ease",
                  border: "2px dashed #0d3b66",
                }}
              >
                {/* Content */}
                <Stack
                  spacing={2}
                  alignItems="center"
                  sx={{
                    position: "relative",
                    zIndex: 1,
                    textAlign: "center",
                  }}
                >
                  {/* Icon */}
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: "50%",
                      backgroundColor: "primary.main",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backdropFilter: "blur(10px)",
                      border: "2px solid white",
                    }}
                  >
                    <CreateIcon sx={{ fontSize: 30, color: "white" }} />
                  </Box>

                  {/* Text */}
                  <Typography
                    variant="h6"
                    sx={{
                      color: "primary.main",
                      fontWeight: 600,
                    }}
                  >
                    Create New Template
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      color: "primary.main",
                      maxWidth: "200px",
                    }}
                  >
                    Design a custom upselling template for your leads
                  </Typography>

                  {/* CTA Button */}
                  <Button
                    variant="contained"
                    startIcon={<AutoAwesomeIcon />}
                    onClick={() =>
                      setOpenManageRecommendedWhatsAppTemplate(
                        INITIAL_WHATSAPP_TEMPLATE_DATA,
                      )
                    }
                    sx={{
                      backgroundColor: "primary.main",
                      color: "white",
                      borderRadius: "20px",
                      cursor: "pointer",
                      px: 3,
                      py: 1,
                      fontWeight: 600,
                      backdropFilter: "blur(10px)",
                      border: "1px solid rgba(255, 255, 255, 0.3)",
                      "&:hover": {
                        transform: "scale(1.05)",
                      },
                    }}
                  >
                    Start Creating
                  </Button>
                </Stack>
              </Box>

              {/* Recommended Templates */}
              {recommendedTemplate.map((template, index) => {
                const convertedTemplate =
                  convertToWhatsAppTemplateData(template);
                const bodyVariableValues = getBodyVariableValues(template);

                return (
                  <Box
                    key={index}
                    sx={{
                      backgroundColor: "#efeae2",
                      backgroundImage: `url(${WhatsappBgImg})`,
                      backgroundRepeat: "repeat",
                      boxShadow: "0 0 10px 0 rgba(0, 0, 0, 0.1)",
                      backgroundSize: "auto",
                      // backgroundPosition: "center",
                      backgroundBlendMode: "multiply",
                      borderRadius: 2,
                      position: "relative",
                      width: "380px",
                      height: "450px",
                      px: 2,
                      pb: 2,
                      pt: 6,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                    }}
                  >
                    <Button
                      variant="contained"
                      sx={{
                        position: "absolute",
                        top: 10,
                        right: 10,
                        cursor: "pointer",
                      }}
                      onClick={() => handleOpenWhatsapp(convertedTemplate)}
                      endIcon={<SendIcon />}
                    >
                      send
                    </Button>
                    {/* <AddIcon
                      sx={{
                        position: "absolute",
                        top: 10,
                        right: 10,
                        cursor: "pointer",
                      }}
                      onClick={() => handleCreateTemplate(convertedTemplate)}
                    /> */}
                    <Typography
                      sx={{
                        position: "absolute",
                        top: 20,
                        left: 20,
                        cursor: "pointer",
                        fontWeight: 700,
                      }}
                    >
                      {template.name}
                    </Typography>

                    <PreviewTemplate
                      template={convertedTemplate}
                      headerType={convertedTemplate.header?.format || "TEXT"}
                      headerVariableValue=""
                      bodyVariableValues={bodyVariableValues}
                      bgImage={true}
                    />
                  </Box>
                );
              })}
            </Box>
          ) : (
            <Box sx={{ textAlign: "center", py: 4, width: "100%" }}>
              <Typography variant="h6" color="text.secondary">
                No recommended templates available
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 1, mb: 2 }}
              >
                We couldn't find any suitable templates for upselling at this
                time.
              </Typography>

              <UpsellAnimationButton
                onClick={onHandleRecommendedTemplatesClick}
                label="Get Recommended Template's"
              />
            </Box>
          )}
        </Box>
      ) : (
        // List/Table View
        <Box>
          {convertedTemplates.length > 0 ? (
            <RecommendedTemplatesTable
              templates={convertedTemplates}
              onTemplateAdd={handleTemplateAdd}
              onTemplatePreview={() => {}} // No preview for recommended templates
            />
          ) : (
            <Box sx={{ textAlign: "center", py: 4, width: "100%" }}>
              <Typography variant="h6" color="text.secondary">
                No recommended templates available
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 1, mb: 2 }}
              >
                We couldn't find any suitable templates for upselling at this
                time.
              </Typography>

              <UpsellAnimationButton
                onClick={onHandleRecommendedTemplatesClick}
                label="Get Recommended Template's"
              />
            </Box>
          )}
        </Box>
      )}

      {openManageRecommendedWhatsAppTemplate && (
        <ManageWhatsAppTemplateRightPanel
          open
          onClose={() => setOpenManageRecommendedWhatsAppTemplate(null)}
          onSaveSuccess={(templateData) => {
            handleTemplateSaveSuccess(templateData);
          }}
          isFromRecommendedFromRightpanel
          recommendedTemplateDataFromRightpanel={
            openManageRecommendedWhatsAppTemplate
          }
        />
      )}

      {validMetaLoading && (
        <LoadingDialogWithMessage
          open={validMetaLoading}
          label="Loading Meta Template Validation..."
        />
      )}

      {openWhatsapp && (
        <ViewWhatsappDetails
          open={true}
          onClose={() => setOpenWhatsapp(null)}
          leadDetails={openWhatsapp}
          message={templateMessage}
        />
      )}
    </PageLoader>
  );
};
